import { useCallback } from 'react';

import { downloadFile, toCsv } from '@cast/utils';

import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';

import { CostReportTableData } from '../types';

const costReportToCsv = (
  data: CostReportTableData[],
  hasGpu: boolean,
  hasTpu: boolean
) => {
  return toCsv<CostReportTableData>(
    {
      date: 'date',
      podCount: {
        header: 'pods',
        map: (value) => value?.total.toFixed(2) || '0',
      },
      normalizedCostPerPod: {
        header: 'cost per pod',
        map: (value) => formatDailyMetricsCost(value),
      },
      countCpu: {
        header: 'provisioned cpu',
        map: (value) => value.total?.toFixed(2) + ' CPU',
      },
      countRamGib: {
        header: 'provisioned memory',
        map: (value) => value.total?.toFixed(2) + ' GiB',
      },
      ...(hasGpu && {
        countGpu: {
          header: 'provisioned gpu',
          map: (value) => value.total?.toFixed(2) + ' GPU',
        },
      }),
      ...(hasTpu && {
        countTpu: {
          header: 'provisioned tpu',
          map: (value) => value.total?.toFixed(2) + ' TPU',
        },
      }),
      totalUptimeCpuCost: {
        header: 'total cpu cost',
        map: (value) => formatDailyMetricsCost(value),
      },
      totalUptimeRamCost: {
        header: 'total ram cost',
        map: (value) => formatDailyMetricsCost(value),
      },
      ...(hasGpu && {
        totalUptimeGpuCost: {
          header: 'total gpu cost',
          map: (value) => formatDailyMetricsCost(value),
        },
      }),
      ...(hasTpu && {
        totalUptimeTpuCost: {
          header: 'total tpu cost',
          map: (value) => formatDailyMetricsCost(value),
        },
      }),
      totalUptimeCost: {
        header: 'total cost',
        map: (value) => formatDailyMetricsCost(value.total),
      },
    },
    data
  );
};

type UseReportDownload = {
  download: () => void;
};

export const useReportDownload = (
  filename: string,
  data: CostReportTableData[],
  { hasGpu, hasTpu }: { hasGpu: boolean; hasTpu: boolean }
): UseReportDownload => {
  const download = useCallback(() => {
    downloadFile(costReportToCsv(data, hasGpu, hasTpu), `${filename}.csv`);
  }, [data, filename, hasGpu, hasTpu]);

  return { download };
};
